import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ResetFiltersButton } from "@/components/common/ResetFiltersButton";
import { OfficeSchedulePanel } from "@/components/command/OfficeSchedulePanel";
import { WeeklyReviewPanel } from "@/components/command/WeeklyReviewPanel";
import { NotebookPanel } from "@/components/command/notebook/NotebookPanel";
import { DiaryPanel } from "@/components/command/DiaryPanel";
import { api } from "@/lib/api";

const TABS = [
  { value: "schedule", label: "Office Schedule" },
  { value: "weekly-review", label: "Weekly Review" },
  { value: "notebook", label: "Founders Notebook" },
  { value: "diary", label: "Founder Diary" },
];
// Accept legacy Command Centre tab keys so old deep links resolve here safely.
const TAB_ALIAS = { office: "schedule", "office-schedule": "schedule", weekly: "weekly-review" };

export default function OfficeTeam() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const mapped = TAB_ALIAS[rawTab] || rawTab;
  const tab = TABS.some((t) => t.value === mapped) ? mapped : "schedule";

  const filter = searchParams.get("filter");
  const person = searchParams.get("person");
  const entryId = searchParams.get("entry");
  const reviewId = searchParams.get("review");
  const noteId = searchParams.get("note");
  const view = searchParams.get("view");

  const changeTab = (v) => {
    const p = new URLSearchParams(searchParams);
    p.set("tab", v);
    ["filter", "person", "entry", "review", "note", "view", "author"].forEach((k) => p.delete(k));
    setSearchParams(p);
  };
  const clearParams = (keys) => { const p = new URLSearchParams(searchParams); keys.forEach((k) => p.delete(k)); setSearchParams(p); };

  const [mentionCount, setMentionCount] = useState(0);
  const refreshMentionCount = useCallback(() => { api.mentionsUnreadCount().then((r) => setMentionCount((prev) => (prev === r.count ? prev : r.count))).catch(() => {}); }, []);
  useEffect(() => { refreshMentionCount(); }, [refreshMentionCount, tab]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="War Room"
        title="Office & Team"
        description="Where the founders plan, meet, work and think together."
      />

      <ResetFiltersButton /><Tabs value={tab} onValueChange={changeTab} className="w-full">
        <TabsList data-testid="office-tabs" className="mb-6 flex h-auto flex-wrap justify-start gap-1.5 rounded-full border border-[#E4E9F3] bg-white p-1.5 shadow-sm">
          {TABS.map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              data-testid={`office-tab-${t.value}`}
              className="rounded-full px-4 py-1.5 text-sm font-semibold text-[#5A6B8C] transition-colors data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=active]:[background-color:var(--accent-primary)]"
            >
              {t.label}
              {t.value === "notebook" && mentionCount > 0 && (
                <span data-testid="office-notebook-count" className="ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-[#E0A82E] px-1.5 text-[10px] font-bold leading-4 text-white">{mentionCount}</span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="schedule"><OfficeSchedulePanel initialPerson={person} sharedOnly={filter === "shared"} openEntryId={entryId} onConsume={() => clearParams(["entry"])} onClearFilter={() => clearParams(["person", "filter"])} /></TabsContent>
        <TabsContent value="weekly-review"><WeeklyReviewPanel initialId={reviewId} initialFilter={filter} initialView={view} navKey={`${reviewId || ""}|${filter || ""}|${view || ""}`} onClearFilter={() => clearParams(["review", "filter", "view"])} /></TabsContent>
        <TabsContent value="notebook"><NotebookPanel initialView={view} openNoteId={noteId} onConsume={() => clearParams(["note"])} onMentionsChanged={refreshMentionCount} /></TabsContent>
        <TabsContent value="diary"><DiaryPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
